export type RovingOrientation = 'horizontal' | 'vertical' | 'both';

/**
 * Resolves the next focus/selection index for a roving-tabindex keyboard
 * pattern (WAI-ARIA), shared by tab lists, accordions and the media gallery.
 *
 * Returns the next index (wrapping at the ends) for the relevant arrow keys
 * plus `Home`/`End`, or `null` when the key is not a navigation key for the
 * given `orientation` — letting callers `return` early without preventing the
 * default behaviour of unrelated keys.
 *
 * @param orientation Which arrow keys participate: `horizontal`
 *   (Left/Right), `vertical` (Up/Down) or `both`.
 */
export function getRovingIndex(
    key: string,
    current: number,
    count: number,
    orientation: RovingOrientation = 'both'
): number | null {
    if (count <= 0) return null;

    const horizontal = orientation !== 'vertical';
    const vertical = orientation !== 'horizontal';

    switch (key) {
        case 'ArrowRight':
            return horizontal ? (current + 1) % count : null;
        case 'ArrowDown':
            return vertical ? (current + 1) % count : null;
        case 'ArrowLeft':
            return horizontal ? (current - 1 + count) % count : null;
        case 'ArrowUp':
            return vertical ? (current - 1 + count) % count : null;
        case 'Home':
            return 0;
        case 'End':
            return count - 1;
        default:
            return null;
    }
}
